"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAppBaseDataSourceBuildingBlockRef = createAppBaseDataSourceBuildingBlockRef;
exports.createAppActionBuildingBlockRef = createAppActionBuildingBlockRef;
exports.createAppBaseCapabilityBuildingBlockRef = createAppBaseCapabilityBuildingBlockRef;
exports.createAppBaseDocumentExportBuildingBlockRef = createAppBaseDocumentExportBuildingBlockRef;
exports.validateAppBaseBuildingBlockRef = validateAppBaseBuildingBlockRef;
exports.isRecognizedAppBaseBuildingBlockRef = isRecognizedAppBaseBuildingBlockRef;
const brand_1 = require("@streamlain/brand");
const app_base_contract_1 = require("./app-base-contract");
const STREAMLAIN_BUILDING_BLOCK_KIND_BY_FAMILY = new Map([
    ['streamlain.datasource', 'dataSource'],
    ['streamlain.action', 'action'],
    ['streamlain.capability', 'capability'],
    ['streamlain.documentExport', 'documentExport'],
]);
const STREAMLAIN_ACTION_INTENTS = new Set([
    'rowInsert',
    'automationRun',
    'attachmentUpdate',
    'rowGroupInsert',
    'ownRowChange',
]);
function createAppBaseDataSourceBuildingBlockRef(input) {
    return parseBuildingBlockRef({
        id: `streamlain.datasource.${input.role}.${input.contentModel}.${input.sourceKey}`,
        kind: 'dataSource',
        family: 'streamlain.datasource',
        label: input.label,
        sourceKey: input.sourceKey,
        metadata: {
            role: input.role,
            contentModel: input.contentModel,
        },
    });
}
function createAppActionBuildingBlockRef(input) {
    return parseBuildingBlockRef({
        id: `streamlain.action.${input.intent}.${input.actionId}`,
        kind: 'action',
        family: 'streamlain.action',
        label: input.label,
        actionId: input.actionId,
        sourceKey: input.sourceKey,
        metadata: {
            intent: input.intent,
        },
    });
}
function createAppBaseCapabilityBuildingBlockRef(input) {
    const scope = input.actionId ?? input.sourceKey ?? input.exportId;
    return parseBuildingBlockRef({
        id: scope
            ? `streamlain.capability.${input.capabilityId}.${scope}`
            : `streamlain.capability.${input.capabilityId}`,
        kind: 'capability',
        family: 'streamlain.capability',
        label: input.label ?? input.capabilityId,
        capabilityId: input.capabilityId,
        actionId: input.actionId,
        sourceKey: input.sourceKey,
        exportId: input.exportId,
    });
}
function createAppBaseDocumentExportBuildingBlockRef(input) {
    return parseBuildingBlockRef({
        id: `streamlain.documentExport.${input.formats.join('+')}.${input.exportId}`,
        kind: 'documentExport',
        family: 'streamlain.documentExport',
        label: input.label,
        exportId: input.exportId,
        actionId: input.targetActionId,
        metadata: {
            formats: [...input.formats],
        },
    });
}
function parseBuildingBlockRef(input) {
    return app_base_contract_1.AppBaseBuildingBlockRefSchema.parse({
        schemaVersion: app_base_contract_1.APP_BASE_BUILDING_BLOCK_REF_SCHEMA_VERSION,
        ...input,
    });
}
function validateAppBaseBuildingBlockRef(ref, path = 'buildingBlockRefs') {
    const issues = [];
    const family = (0, app_base_contract_1.appBaseBuildingBlockFamily)(ref);
    if (!family) {
        issues.push({
            path,
            message: `App building block ref ${ref.id} must use a ${brand_1.BRAND_DISPLAY_NAME} building block family.`,
        });
        return issues;
    }
    const expectedKind = STREAMLAIN_BUILDING_BLOCK_KIND_BY_FAMILY.get(family);
    if (expectedKind && ref.kind !== expectedKind) {
        issues.push({
            path: `${path}.kind`,
            message: `App building block ref ${ref.id} must use kind ${expectedKind}.`,
        });
    }
    if (family === 'streamlain.datasource') {
        validateDataSourceRef(ref, path, issues);
    }
    else if (family === 'streamlain.action') {
        validateActionRef(ref, path, issues);
    }
    else if (family === 'streamlain.capability') {
        validateCapabilityRef(ref, path, issues);
    }
    else if (family === 'streamlain.documentExport') {
        validateDocumentExportRef(ref, path, issues);
    }
    return issues;
}
function isRecognizedAppBaseBuildingBlockRef(ref) {
    return validateAppBaseBuildingBlockRef(ref).length === 0;
}
function validateDataSourceRef(ref, path, issues) {
    const role = ref.metadata?.['role'];
    const contentModel = ref.metadata?.['contentModel'];
    if (!ref.sourceKey) {
        issues.push({
            path: `${path}.sourceKey`,
            message: `Data source building block ${ref.id} must declare sourceKey.`,
        });
    }
    if (typeof role !== 'string' || typeof contentModel !== 'string') {
        issues.push({
            path: `${path}.metadata`,
            message: `Data source building block ${ref.id} must declare role and contentModel metadata.`,
        });
        return;
    }
    const expectedId = (0, app_base_contract_1.canonicalAppBaseBuildingBlockRefId)(ref);
    if (expectedId && ref.id !== expectedId) {
        issues.push({
            path: `${path}.id`,
            message: `Data source building block ${ref.id} must use canonical id ${expectedId}.`,
        });
    }
}
function validateActionRef(ref, path, issues) {
    const intent = ref.metadata?.['intent'];
    if (!ref.actionId) {
        issues.push({
            path: `${path}.actionId`,
            message: `Action building block ${ref.id} must declare actionId.`,
        });
    }
    if (typeof intent !== 'string' || !STREAMLAIN_ACTION_INTENTS.has(intent)) {
        issues.push({
            path: `${path}.metadata.intent`,
            message: `Action building block ${ref.id} must declare a supported action intent.`,
        });
    }
    const expectedId = (0, app_base_contract_1.canonicalAppBaseBuildingBlockRefId)(ref);
    if (expectedId && ref.id !== expectedId) {
        issues.push({
            path: `${path}.id`,
            message: `Action building block ${ref.id} must use canonical id ${expectedId}.`,
        });
    }
}
function validateCapabilityRef(ref, path, issues) {
    if (!ref.capabilityId) {
        issues.push({
            path: `${path}.capabilityId`,
            message: `Capability building block ${ref.id} must declare capabilityId.`,
        });
        return;
    }
    if (!ref.id.startsWith(`streamlain.capability.${ref.capabilityId}`)) {
        issues.push({
            path: `${path}.id`,
            message: `Capability building block ${ref.id} must start with streamlain.capability.${ref.capabilityId}.`,
        });
    }
    const expectedId = (0, app_base_contract_1.canonicalAppBaseBuildingBlockRefId)(ref);
    if (ref.id !== expectedId) {
        issues.push({
            path: `${path}.id`,
            message: `Capability building block ${ref.id} must use canonical id ${expectedId}.`,
        });
    }
    if ((ref.capabilityId === 'dataset.readWindow' ||
        ref.capabilityId === 'dataset.subscribe') &&
        !ref.sourceKey) {
        issues.push({
            path: `${path}.sourceKey`,
            message: `Dataset capability building block ${ref.id} must declare sourceKey.`,
        });
    }
    if ((ref.capabilityId === 'database.row.insert' ||
        ref.capabilityId === 'agentAction.invoke' ||
        ref.capabilityId === 'attachment.update' ||
        ref.capabilityId === 'automation.run') &&
        !ref.actionId) {
        issues.push({
            path: `${path}.actionId`,
            message: `Action capability building block ${ref.id} must declare actionId.`,
        });
    }
    if (ref.capabilityId === 'document.export' &&
        !ref.actionId &&
        !ref.exportId) {
        issues.push({
            path: `${path}.exportId`,
            message: `Document export capability building block ${ref.id} must declare exportId or actionId.`,
        });
    }
}
function validateDocumentExportRef(ref, path, issues) {
    const formats = ref.metadata?.['formats'];
    if (!ref.exportId) {
        issues.push({
            path: `${path}.exportId`,
            message: `Document export building block ${ref.id} must declare exportId.`,
        });
    }
    if (!Array.isArray(formats) ||
        formats.length === 0 ||
        !formats.every(format => typeof format === 'string' && format.length > 0)) {
        issues.push({
            path: `${path}.metadata.formats`,
            message: `Document export building block ${ref.id} must declare formats metadata.`,
        });
        return;
    }
    const expectedId = (0, app_base_contract_1.canonicalAppBaseBuildingBlockRefId)(ref);
    if (expectedId && ref.id !== expectedId) {
        issues.push({
            path: `${path}.id`,
            message: `Document export building block ${ref.id} must use canonical id ${expectedId}.`,
        });
    }
}
